
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "@/hooks/useNotify";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { logAuthTransition } from "@/utils/authStateMonitor";
import { useUserInvitationStatus } from "@/hooks/useUserInvitationStatus";

export const useProfileSetup = () => {
  const { user, profile, refreshProfile, setProfile } = useAuth();
  const { pendingInvitations } = useUserInvitationStatus();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyRole, setCompanyRole] = useState("");
  const [personalPhoneNumber, setPersonalPhoneNumber] = useState("");
  const [website, setWebsite] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [suburb, setSuburb] = useState("");
  const [city, setCity] = useState("");
  const [provinceState, setProvinceState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [createNewCompany, setCreateNewCompany] = useState(true);
  
  // Join company states
  const [inviteToken, setInviteToken] = useState("");
  
  // Invited user detection
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [invitedCompany, setInvitedCompany] = useState<any>(null);

  const navigate = useNavigate();
  const notify = useNotify();

  useEffect(() => {
    if (user?.user_metadata) {
      setFirstName(user.user_metadata.first_name || "");
      setLastName(user.user_metadata.last_name || "");
    }
    
    if (profile) {
      setFirstName(profile.first_name || firstName);
      setLastName(profile.last_name || lastName);
      setPersonalPhoneNumber(profile.personal_phone_number || "");
      setCompanyRole(profile.company_position || "");
      
      if (profile.profile_photo_url) {
        setProfilePhotoUrl(profile.profile_photo_url);
      }
    }
  }, [user, profile]);

  // Check for accepted invitations (invited user mode)
  useEffect(() => {
    const checkAcceptedInvitations = async () => {
      if (!user?.email) return;
      
      try {
        // Check both invitation tables for accepted invitations
        const { data: acceptedInvitations, error } = await supabase
          .from('company_invitations')
          .select('*, companies(*)')
          .eq('email', user.email)
          .eq('status', 'accepted')
          .limit(1);
          
        if (error) throw error;
        
        if (acceptedInvitations && acceptedInvitations.length > 0) {
          const invitation = acceptedInvitations[0];
          setIsInvitedUser(true);
          setInvitedCompany(invitation.companies);
          console.log('Detected invited user with company:', invitation.companies);
        }
      } catch (error) {
        console.error('Error checking accepted invitations:', error);
      }
    };

    checkAcceptedInvitations();
  }, [user?.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      notify.error("Authentication error — You must be logged in to complete your profile.");
      return;
    }

    // Handle invited user differently - they just complete personal profile
    if (isInvitedUser) {
      return await handleInvitedUserSubmit();
    }

    // Validate based on selected tab for non-invited users
    if (createNewCompany) {
      if (!companyName?.trim()) {
        notify.error("Validation error — Company name is required when creating a new company.");
        return;
      }
    } else {
      if (!inviteToken?.trim()) {
        notify.error("Validation error — Please enter your invitation token to join a company.");
        return;
      }
    }
    
    setIsLoading(true);
    
    try {
      let companyId = null;
      
      if (createNewCompany) {
        // ── Steps 1+2: Create company and add member atomically ───────

        const { data: newCompanyId, error: companyError } = await supabase
          .rpc('create_company_with_member', {
            p_name: companyName,
            p_email: user.email,
            p_website: website || null,
            p_street_address: streetAddress || null,
            p_suburb: suburb || null,
            p_city: city || null,
            p_province_state: provinceState || null,
            p_postal_code: postalCode || null,
            p_logo_url: logoUrl || null,
          });

        if (companyError) throw companyError;

        if (!newCompanyId) throw new Error('Company creation failed — no ID returned');

        companyId = newCompanyId;

        logAuthTransition(
          'company_created',
          user.id,
          { companyId, companyName },
          'profile_setup'
        );
      } else {
        // Join existing company flow
        console.log('Processing join company with token:', inviteToken);
        
        // First check company_invitations (new system)
        let invitationData = await supabase
          .from('company_invitations')
          .select('*, companies(*)')
          .eq('token', inviteToken)
          .eq('status', 'pending')
          .gt('expires_at', new Date().toISOString())
          .maybeSingle();

        // If not found, check legacy invitations table
        if (!invitationData.data) {
          invitationData = await supabase
            .from('invitations')
            .select('*, companies(*)')
            .eq('token', inviteToken)
            .eq('status', 'pending')
            .gt('expires_at', new Date().toISOString())
            .maybeSingle();
        }

        if (invitationData.error) {
          console.error('Error fetching invitation:', invitationData.error);
          throw new Error('Failed to validate invitation token');
        }

        if (!invitationData.data) {
          throw new Error('Invalid or expired invitation token');
        }

        const invitation = invitationData.data;
        companyId = invitation.company_id;

        // Check if user email matches invitation
        if (invitation.email !== user.email) {
          throw new Error('This invitation was sent to a different email address');
        }

        // Check if user is already a member
        const { data: existingMember } = await supabase
          .from('company_members')
          .select('id')
          .eq('user_id', user.id)
          .eq('company_id', companyId)
          .maybeSingle();

        if (!existingMember) {
          // Add user as company member
          const { error: memberError } = await supabase
            .from('company_members')
            .insert({
              company_id: companyId,
              user_id: user.id,
              role: 'member'
            });

          if (memberError) {
            console.error('Error adding company member:', memberError);
            throw memberError;
          }
        }

        // Update invitation status
        const tableName = invitation.hasOwnProperty('invited_by') ? 'company_invitations' : 'invitations';
        const { error: updateError } = await supabase
          .from(tableName)
          .update({ status: 'accepted' })
          .eq('id', invitation.id);

        if (updateError) {
          console.error('Error updating invitation status:', updateError);
          // Don't throw here as the important part (joining) is done
        }

        // Log company joining
        logAuthTransition(
          'company_joined',
          user.id,
          { companyId, inviteToken },
          'profile_setup'
        );
      }
      
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          company_position: companyRole,
          personal_phone_number: personalPhoneNumber,
          profile_photo_url: profilePhotoUrl,
          company_id: companyId,
          profile_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq("id", user.id);
      
      if (error) throw error;

      // Immediately update AuthContext profile state to prevent race condition
      if (profile) {
        setProfile({
          ...profile,
          first_name: firstName,
          last_name: lastName,
          company_position: companyRole,
          personal_phone_number: personalPhoneNumber,
          profile_photo_url: profilePhotoUrl,
          company_id: companyId,
          profile_completed: true,
          status: 'active'
        });
      }
      
      // Log profile completion
      logAuthTransition(
        'profile_setup_completed',
        user.id,
        { companyId, createNewCompany },
        'profile_setup'
      );

      // Refresh profile data to sync with AuthContext
      if (refreshProfile) {
        await refreshProfile();
      }

      notify.success("Profile setup complete — Your account is ready to use.");
      
      navigate("/dashboard", { replace: true });
    } catch (error: any) {
      console.error("Profile setup error:", {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
        status: error.status,
        fullError: error,
        createNewCompany,
        inviteToken: createNewCompany ? undefined : inviteToken
      });
      
      notify.error(`Profile setup failed — ${error.message || "An error occurred. Please try again."}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvitedUserSubmit = async () => {
    if (!user?.id || !profile?.company_id) {
      notify.error("Error — User company information not found.");
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          company_position: companyRole,
          personal_phone_number: personalPhoneNumber,
          profile_photo_url: profilePhotoUrl,
          profile_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq("id", user.id);
      
      if (error) throw error;

      // Update AuthContext profile state
      if (profile) {
        setProfile({
          ...profile,
          first_name: firstName,
          last_name: lastName,
          company_position: companyRole,
          personal_phone_number: personalPhoneNumber,
          profile_photo_url: profilePhotoUrl,
          profile_completed: true,
          status: 'active'
        });
      }
      
      // Log profile completion
      logAuthTransition(
        'profile_setup_completed',
        user.id,
        { companyId: profile.company_id, isInvitedUser: true },
        'profile_setup'
      );

      // Refresh profile data
      if (refreshProfile) {
        await refreshProfile();
      }

      notify.success("Profile setup complete — Welcome to your company! Your account is ready to use.");
      
      navigate("/dashboard", { replace: true });
    } catch (error: any) {
      console.error("Invited user profile setup error:", error);
      
      notify.error(`Profile setup failed — ${error.message || "An error occurred. Please try again."}`);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    companyName,
    setCompanyName,
    companyRole,
    setCompanyRole,
    personalPhoneNumber,
    setPersonalPhoneNumber: setPersonalPhoneNumber,
    website,
    setWebsite,
    streetAddress,
    setStreetAddress,
    suburb,
    setSuburb,
    city,
    setCity,
    provinceState,
    setProvinceState,
    postalCode,
    setPostalCode,
    logoUrl,
    setLogoUrl,
    profilePhotoUrl,
    setProfilePhotoUrl,
    isLoading,
    createNewCompany,
    setCreateNewCompany,
    handleSubmit,
    
    // Join company fields
    inviteToken,
    setInviteToken,
    
    // Invited user fields
    isInvitedUser,
    invitedCompany,
  };
};
